import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar, Users, Briefcase, LogOut, Plus, MessageSquare, ShoppingBag, Search, Sparkles, Wand2, ClipboardList, MapPin, DollarSign, Eye, Clock, Megaphone, Trash2, User, CalendarDays } from 'lucide-react';
import { api } from '../App';
import { toast } from 'sonner';
import Footer from '../components/Footer';
import StripeCheckout from '../components/StripeCheckout';
import AccountTools from '../components/AccountTools';

const emptyJobForm = {
  title: '',
  description: '',
  responsibilities: '',
  requirements: '',
  salary_range: '',
  location: '',
  workplace_type: 'Remote',
  employment_type: 'Full-time',
  seniority: 'Mid-level',
  interview_format: 'Live video interview',
  benefits: '',
  ai_interview_mode: 'optional',
  ai_interview_threshold: 75,
  ai_interview_focus: '',
  ai_custom_questions: '',
};

const boostPackages = [
  { days: 7, price: 19, label: 'Local Spotlight', note: 'Featured placement for one week. Good for urgent interviews.' },
  { days: 30, price: 49, label: 'Regional SEO Boost', note: 'Best value against $150+/month job-board sponsored minimums.' },
  { days: 60, price: 89, label: 'Hiring Campaign', note: 'Longer visibility for harder-to-fill roles.' },
];

const emptyActivityForm = {
  title: '',
  description: '',
  activity_type: 'Meet and greet',
  location: 'Online',
  audience: 'Candidates and potential clients',
  scheduled_at: '',
  call_to_action: 'I am interested',
};

const activityTemplates = [
  { label: 'Open House', title: 'Virtual Hiring Open House', activity_type: 'Open house', description: 'Meet our team, hear how we work, and ask questions before booking an interview.', call_to_action: 'Join open house' },
  { label: 'Culture Chat', title: 'Team Culture Chat', activity_type: 'Culture chat', description: 'A casual session where candidates and potential clients can learn what our company values and how we serve people.', call_to_action: 'Save my seat' },
  { label: 'Skill Challenge', title: 'Real-World Skill Challenge', activity_type: 'Skill challenge', description: 'Try a short activity based on the work we do so both sides can understand fit before a formal interview.', call_to_action: 'Try activity' },
];
const jobTemplates = [
  {
    label: 'Sales',
    title: 'Sales Development Representative',
    description: 'Own first conversations with prospective customers, qualify opportunities, and help the team build a strong pipeline through clear outreach and thoughtful follow-up.',
    responsibilities: 'Prospect new accounts, book qualified meetings, update CRM notes, partner with account executives, prepare candidates for video interview expectations',
    requirements: 'Outbound sales, CRM experience, strong communication, objection handling, coachable mindset',
    salary_range: '$50,000 - $75,000 + commission',
    benefits: 'Performance bonuses, paid training, career path to account executive',
  },
  {
    label: 'Operations',
    title: 'Operations Coordinator',
    description: 'Keep daily workflows organized across customers, vendors, and internal teams while improving how work moves from request to completion.',
    responsibilities: 'Track projects, coordinate schedules, prepare reports, resolve process blockers, maintain accurate documentation',
    requirements: 'Operations support, scheduling, Microsoft Office or Google Workspace, attention to detail, customer communication',
    salary_range: '$45,000 - $65,000',
    benefits: 'Health benefits, paid time off, growth into operations management',
  },
  {
    label: 'Customer Success',
    title: 'Customer Success Specialist',
    description: 'Support customers after onboarding, answer questions quickly, and turn customer feedback into better retention and product adoption.',
    responsibilities: 'Run check-ins, answer support questions, document customer needs, flag churn risk, coordinate with sales and operations',
    requirements: 'Customer support, relationship building, problem solving, written communication, SaaS or service experience',
    salary_range: '$48,000 - $70,000',
    benefits: 'Remote flexibility, professional development, team performance incentives',
  },
];

const splitJobDescription = (description = '') => {
  const parts = description.split(/\n+/).map((part) => part.trim()).filter(Boolean);
  const firstPlainPart = parts.find((part) => !part.includes(':')) || parts[0] || '';
  const firstSentence = firstPlainPart.match(/^.{80,220}?[.!?](\s|$)/)?.[0]?.trim();
  const summary = firstSentence || firstPlainPart.slice(0, 220);
  const detailLines = parts.filter((part) => part.includes(':'));
  return { summary, detailLines, parts };
};

const formatSlotTime = (value) => {
  if (!value) return 'Time not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const toDateInput = (date) => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const tomorrow = new Date();
tomorrow.setDate(tomorrow.getDate() + 1);
const thirtyDaysOut = new Date(tomorrow);
thirtyDaysOut.setDate(thirtyDaysOut.getDate() + 30);

const defaultBulkSchedule = {
  start_date: toDateInput(tomorrow),
  end_date: toDateInput(thirtyDaysOut),
  start_time: '09:00',
  end_time: '16:30',
  duration_minutes: 30,
  break_minutes: 0,
  weekdays: [0, 1, 2, 3, 4],
};

const weekdayOptions = [
  { value: 0, label: 'Mon' }, { value: 1, label: 'Tue' }, { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' }, { value: 4, label: 'Fri' }, { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
];

export default function CompanyDashboard({ user }) {
  const [jobs, setJobs] = useState([]);
  const [slots, setSlots] = useState([]);
  const [aiInterviews, setAiInterviews] = useState([]);
  const [activities, setActivities] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [showCreateJob, setShowCreateJob] = useState(false);
  const [showCreateSlot, setShowCreateSlot] = useState(false);
  const [showCreateActivity, setShowCreateActivity] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedListing, setSelectedListing] = useState(null);
  const [boostCheckout, setBoostCheckout] = useState(null);
  const [showBoostCheckout, setShowBoostCheckout] = useState(false);
  const [selectedBoostJob, setSelectedBoostJob] = useState(null);
  const [showBoostPackages, setShowBoostPackages] = useState(false);
  const [loading, setLoading] = useState(false);
  const [jobForm, setJobForm] = useState(emptyJobForm);
  const [activityForm, setActivityForm] = useState(emptyActivityForm);
  const [slotMode, setSlotMode] = useState('single');
  const [bulkSchedule, setBulkSchedule] = useState(defaultBulkSchedule);
  const [aiSchedulePrompt, setAiSchedulePrompt] = useState('Weekdays from tomorrow for 30 days, 9:00 AM to 4:30 PM, every 30 minutes');
  const [aiSlotPreview, setAiSlotPreview] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      loadJobs();
      loadActivities();
      loadBookings();
      loadAIInterviews();
    }
  }, [user]);

  const loadJobs = async () => {
    try {
      const res = await api.get(`/jobs/company/${user.id}`);
      setJobs(res.data);
      loadSlotsForJobs(res.data);
    } catch (error) {
      toast.error('Failed to load jobs');
    }
  };

  const loadSlotsForJobs = async (jobList = jobs) => {
    try {
      if (!jobList.length) {
        setSlots([]);
        return;
      }

      const responses = await Promise.all(
        jobList.map((job) => api.get(`/slots/${job.id}`).catch(() => ({ data: [] })))
      );

      const openSlots = responses
        .flatMap((res, index) => (
          (res.data || [])
            .filter((slot) => !slot.is_booked)
            .map((slot) => ({ ...slot, job: jobList[index] }))
        ))
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

      setSlots(openSlots);
    } catch (error) {
      toast.error('Failed to load time slots');
    }
  };

  const loadActivities = async () => {
    try {
      const res = await api.get(`/company/activities/company/${user.id}`);
      setActivities(res.data);
    } catch (error) {
      toast.error('Failed to load activities');
    }
  };

  const loadAIInterviews = async () => {
    try {
      const res = await api.get(`/ai-interviews/company/${user.id}`);
      setAiInterviews(res.data);
    } catch (error) {
      console.error('Failed to load AI interviews');
    }
  };

  const loadBookings = async () => {
    try {
      const res = await api.get(`/bookings/company/${user.id}`);
      setBookings(res.data);
    } catch (error) {
      toast.error('Failed to load bookings');
    }
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.target);
    const requirements = formData.get('requirements').split(',').map(r => r.trim()).filter(Boolean);
    const responsibilities = formData.get('responsibilities')?.trim();
    const benefits = formData.get('benefits')?.trim();
    const details = [
      formData.get('description')?.trim(),
      responsibilities && `Key responsibilities: ${responsibilities}`,
      `Workplace: ${formData.get('workplace_type')}`,
      `Employment type: ${formData.get('employment_type')}`,
      `Seniority: ${formData.get('seniority')}`,
      `Interview format: ${formData.get('interview_format')}`,
      benefits && `Benefits and perks: ${benefits}`,
    ].filter(Boolean).join('\n\n');

    const data = {
      title: formData.get('title'),
      description: details,
      requirements,
      salary_range: formData.get('salary_range'),
      location: formData.get('location'),
      ai_interview_mode: formData.get('ai_interview_mode') || 'optional',
      ai_interview_threshold: Number(formData.get('ai_interview_threshold') || 75),
      ai_interview_focus: formData.get('ai_interview_focus') || '',
      ai_custom_questions: (formData.get('ai_custom_questions') || '').split('\n').map((item) => item.trim()).filter(Boolean),
    };

    try {
      await api.post('/jobs', data);
      toast.success('Job created successfully');
      setShowCreateJob(false);
      loadJobs();
      setJobForm(emptyJobForm);
    } catch (error) {
      toast.error('Failed to create job');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateActivity = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/company/activities', { ...activityForm, scheduled_at: activityForm.scheduled_at || null });
      toast.success('Activity created successfully');
      setShowCreateActivity(false);
      setActivityForm(emptyActivityForm);
      loadActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create activity');
    } finally {
      setLoading(false);
    }
  };
  const handleCreateSlot = async (e) => {
    e.preventDefault();

    const formData = new FormData(e.target);
    const startTime = formData.get('start_time');
    const endTime = formData.get('end_time');

    if (!selectedJob?.id) {
      toast.error('Choose a job before adding a time slot');
      return;
    }

    if (!startTime || !endTime) {
      toast.error('Add both a start time and an end time');
      return;
    }

    if (new Date(endTime) <= new Date(startTime)) {
      toast.error('End time must be after start time');
      return;
    }

    setLoading(true);

    const data = {
      job_id: selectedJob.id,
      start_time: startTime,
      end_time: endTime,
    };

    try {
      const res = await api.post('/slots', data);
      toast.success('Time slot created successfully');
      setSlots((currentSlots) => (
        [...currentSlots, { ...res.data, job: selectedJob }]
          .filter((slot, index, allSlots) => allSlots.findIndex((item) => item.id === slot.id) === index)
          .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
      ));
      setShowCreateSlot(false);
      e.target.reset();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create slot');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    navigate('/');
  };

  const updateBulkSchedule = (field, value) => {
    setBulkSchedule((current) => ({ ...current, [field]: value }));
    setAiSlotPreview((current) => current ? { ...current, slot_count: null, preview: [] } : null);
  };

  const toggleScheduleWeekday = (weekday) => {
    setBulkSchedule((current) => ({
      ...current,
      weekdays: current.weekdays.includes(weekday)
        ? current.weekdays.filter((day) => day !== weekday)
        : [...current.weekdays, weekday].sort(),
    }));
    setAiSlotPreview((current) => current ? { ...current, slot_count: null, preview: [] } : null);
  };

  const previewAISchedule = async () => {
    if (!selectedJob?.id) {
      toast.error('Choose a job before building availability');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/slots/ai-preview', {
        job_id: selectedJob.id,
        prompt: aiSchedulePrompt,
      });
      setBulkSchedule(res.data.settings);
      setAiSlotPreview(res.data);
      toast.success(`AI prepared ${res.data.slot_count} interview times`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI could not understand that schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBulkSlots = async () => {
    if (!selectedJob?.id) {
      toast.error('Choose a job before adding time slots');
      return;
    }
    if (!bulkSchedule.weekdays.length) {
      toast.error('Choose at least one day of the week');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/slots/bulk', { ...bulkSchedule, job_id: selectedJob.id });
      const { created_count: created, skipped_duplicates: skipped } = res.data;
      toast.success(`${created} time slot${created === 1 ? '' : 's'} created${skipped ? `; ${skipped} duplicate${skipped === 1 ? '' : 's'} skipped` : ''}`);
      await loadSlotsForJobs(jobs);
      setShowCreateSlot(false);
      setAiSlotPreview(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create time slots');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteJob = async (job) => {
    const confirmed = window.confirm(
      `Delete "${job.title}"? This removes the listing, its open time slots, bookings, and Phase 1 AI interview reports for this job.`
    );
    if (!confirmed) return;

    try {
      await api.delete(`/jobs/${job.id}`);
      toast.success('Job deleted');
      loadJobs();
      loadBookings();
      loadAIInterviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete job');
    }
  };

  const handleBoostJob = (job) => {
    setSelectedBoostJob(job);
    setShowBoostPackages(true);
  };

  const startBoostPackage = async (pkg) => {
    if (!selectedBoostJob) return;

    try {
      const res = await api.post(`/jobs/${selectedBoostJob.id}/boost?days=${pkg.days}`);
      if (res.data.admin_bypass) {
        toast.success(`Admin boosted job for ${pkg.days} days`);
        setShowBoostPackages(false);
        setSelectedBoostJob(null);
        loadJobs();
        return;
      }

      setBoostCheckout({
        jobId: selectedBoostJob.id,
        days: pkg.days,
        clientSecret: res.data.client_secret,
        amount: res.data.amount,
        description: `${pkg.label}: featured job and SEO boost for ${pkg.days} days`,
      });
      setShowBoostPackages(false);
      setShowBoostCheckout(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start boost checkout');
    }
  };
  const updateJobForm = (field, value) => {
    setJobForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateActivityForm = (field, value) => {
    setActivityForm((prev) => ({ ...prev, [field]: value }));
  };

  const applyActivityTemplate = (template) => {
    setActivityForm((prev) => ({ ...prev, ...template }));
  };
  const applyJobTemplate = (template) => {
    setJobForm((prev) => ({
      ...prev,
      ...template,
      location: prev.location || 'Remote',
      workplace_type: prev.workplace_type || 'Remote',
      employment_type: prev.employment_type || 'Full-time',
      seniority: prev.seniority || 'Mid-level',
      interview_format: prev.interview_format || 'Live video interview',
    }));
  };

  const improveJobDescription = () => {
    const title = jobForm.title || 'this role';
    const workplace = jobForm.workplace_type.toLowerCase();
    setJobForm((prev) => ({
      ...prev,
      description: prev.description || `We are hiring ${title} to help our team move faster, serve people well, and bring clear ownership to the work. This ${workplace} role is built for someone who communicates clearly, follows through, and is ready to interview directly with the hiring team.`,
      responsibilities: prev.responsibilities || 'Own the day-to-day work for the role, communicate updates clearly, solve problems before they become delays, collaborate with team members and customers, prepare for structured video interviews',
      requirements: prev.requirements || 'Clear communication, reliable follow-through, relevant experience, comfort with video interviews, organized work style',
      benefits: prev.benefits || 'Competitive pay, interview-first hiring process, growth opportunities, supportive team culture',
    }));
  };

  const handleBoostPaymentSuccess = async (paymentIntent) => {
    if (!boostCheckout) return;

    try {
      await api.post(`/jobs/${boostCheckout.jobId}/boost/confirm`, {
        payment_intent_id: paymentIntent.id,
      });
      toast.success(`Job boosted for ${boostCheckout.days} days!`);
      setShowBoostCheckout(false);
      setBoostCheckout(null);
      loadJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment succeeded, but boost activation failed');
    }
  };

  const reviewAIInterview = async (sessionId, status) => {
    try {
      await api.post(`/ai-interviews/${sessionId}/review`, {
        status,
        notes: status === 'advanced' ? 'Advanced to Phase 2 human interview.' : '',
      });
      toast.success('AI interview updated');
      loadAIInterviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update AI interview');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="container mx-auto flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-4 lg:gap-8">
            <div className="flex items-center gap-3">
              <img src="/logo.svg" alt="The Interview Logo" className="w-10 h-10" />
              <h1 className="hidden text-2xl font-heading font-bold sm:block">The Interview</h1>
            </div>
            <nav className="hidden gap-6 xl:flex">
              <button onClick={() => navigate('/browse-candidates')} className="flex items-center gap-2 text-sm hover:text-blue-600" data-testid="nav-browse-candidates-btn">
                <Search size={16} />
                Candidate Profiles
              </button>
              <button onClick={() => navigate('/profile')} className="flex items-center gap-2 text-sm hover:text-blue-600" data-testid="nav-profile-btn">
                <User size={16} />
                My Profile
              </button>
              <button onClick={() => navigate('/community')} className="flex items-center gap-2 text-sm hover:text-blue-600" data-testid="nav-community-btn">
                <MessageSquare size={16} />
                Community
              </button>
              <button onClick={() => navigate('/marketplace')} className="flex items-center gap-2 text-sm hover:text-blue-600" data-testid="nav-marketplace-btn">
                <ShoppingBag size={16} />
                Marketplace
              </button>
            </nav>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <span className="hidden text-sm text-muted-foreground lg:inline">{user?.full_name}</span>
            <AccountTools onNavigate={navigate} />
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut size={16} className="sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
          <nav className="flex w-full gap-5 overflow-x-auto border-t pt-3 text-sm xl:hidden">
            <button onClick={() => navigate('/browse-candidates')} className="flex shrink-0 items-center gap-2"><Search size={16} /> Candidates</button>
            <button onClick={() => navigate('/profile')} className="flex shrink-0 items-center gap-2"><User size={16} /> Profile</button>
            <button onClick={() => navigate('/community')} className="flex shrink-0 items-center gap-2"><MessageSquare size={16} /> Community</button>
            <button onClick={() => navigate('/marketplace')} className="flex shrink-0 items-center gap-2"><ShoppingBag size={16} /> Marketplace</button>
          </nav>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 sm:px-6 sm:py-8">
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 border" data-testid="stat-card-jobs">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded flex items-center justify-center">
                <Briefcase className="text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{jobs.length}</div>
                <div className="text-sm text-muted-foreground">Active Listings</div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 border" data-testid="stat-card-bookings">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded flex items-center justify-center">
                <Calendar className="text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{bookings.length}</div>
                <div className="text-sm text-muted-foreground">Upcoming Interviews</div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 border" data-testid="stat-card-candidates">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded flex items-center justify-center">
                <Users className="text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{bookings.length}</div>
                <div className="text-sm text-muted-foreground">Candidates</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid xl:grid-cols-[minmax(0,1.25fr)_minmax(360px,.75fr)] gap-8 items-start">
          <div className="bg-white p-6 border">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-heading font-semibold">Job Listings</h2>
                <p className="text-sm text-muted-foreground">Active posts candidates can discover and book from.</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => navigate('/browse-candidates')} data-testid="browse-from-dashboard-btn">
                  <Search size={16} className="mr-2" />
                  Candidate Profiles
                </Button>
                <Button size="sm" onClick={() => setShowCreateJob(true)} data-testid="create-job-btn">
                  <Plus size={16} className="mr-2" />
                  Create Job
                </Button>
              </div>
            </div>

            <div className="grid gap-4" data-testid="jobs-list">
              {jobs.length === 0 ? (
                <div className="border bg-slate-50 p-8 text-center">
                  <Briefcase className="mx-auto mb-3 text-slate-400" />
                  <p className="font-medium">No live listings yet</p>
                  <p className="mt-1 text-sm text-muted-foreground">Create a role, add interview slots, then candidates can book directly.</p>
                </div>
              ) : (
                jobs.map((job) => {
                  const { summary } = splitJobDescription(job.description);
                  const jobSlots = slots.filter((slot) => slot.job_id === job.id);

                  return (
                    <div key={job.id} className="border p-4 bg-white" data-testid={`job-card-${job.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="font-semibold leading-tight">{job.title}</h3>
                          <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-600">
                            <span className="inline-flex items-center gap-1 rounded border bg-slate-50 px-2 py-1">
                              <MapPin size={12} />
                              {job.location || 'Location not set'}
                            </span>
                            {job.salary_range && (
                              <span className="inline-flex items-center gap-1 rounded border bg-slate-50 px-2 py-1">
                                <DollarSign size={12} />
                                {job.salary_range}
                              </span>
                            )}
                          </div>
                        </div>
                        {job.is_boosted && (
                          <span className="shrink-0 text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">
                            Boosted
                          </span>
                        )}
                      </div>

                      <p className="mt-3 text-sm leading-6 text-slate-700">
                        {summary}{job.description?.length > summary.length ? '...' : ''}
                      </p>

                      {Array.isArray(job.requirements) && job.requirements.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {job.requirements.slice(0, 4).map((requirement) => (
                            <span key={requirement} className="rounded border bg-slate-50 px-2 py-1 text-xs text-slate-700">
                              {requirement}
                            </span>
                          ))}
                          {job.requirements.length > 4 && (
                            <span className="rounded border bg-slate-50 px-2 py-1 text-xs text-slate-500">
                              +{job.requirements.length - 4} more
                            </span>
                          )}
                        </div>
                      )}

                      <div className="mt-4 border-t pt-3" data-testid={`job-slots-${job.id}`}>
                        <div className="mb-2 flex items-center justify-between gap-3">
                          <p className="text-sm font-semibold">Available slots</p>
                          <span className="rounded border bg-slate-50 px-2 py-1 text-xs text-slate-600">
                            {jobSlots.length} open
                          </span>
                        </div>
                        {jobSlots.length === 0 ? (
                          <p className="text-sm text-muted-foreground">
                            No open slots for this listing yet. Add one below and it will appear here.
                          </p>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {jobSlots.slice(0, 4).map((slot) => (
                              <span key={slot.id} className="rounded border bg-green-50 px-2 py-1 text-xs text-green-800">
                                {formatSlotTime(slot.start_time)}
                              </span>
                            ))}
                            {jobSlots.length > 4 && (
                              <span className="rounded border bg-slate-50 px-2 py-1 text-xs text-slate-600">
                                +{jobSlots.length - 4} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2 border-t pt-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedListing(job)}
                          data-testid={`view-listing-btn-${job.id}`}
                        >
                          <Eye size={15} className="mr-2" />
                          View Listing
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => {
                            setSelectedJob(job);
                            setShowCreateSlot(true);
                          }}
                          data-testid={`add-slot-btn-${job.id}`}
                        >
                          <Clock size={15} className="mr-2" />
                          Add Time Slot
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="bg-amber-50 border-amber-600 hover:bg-amber-100"
                          onClick={() => handleBoostJob(job)}
                          data-testid={`boost-job-btn-${job.id}`}
                        >
                          Boost Job
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-200 text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteJob(job)}
                          data-testid={`delete-job-btn-${job.id}`}
                        >
                          <Trash2 size={15} className="mr-2" />
                          Delete Job
                        </Button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="bg-white p-6 border">
            <div className="mb-6">
              <h2 className="text-xl font-heading font-semibold">Upcoming Interviews</h2>
              <p className="text-sm text-muted-foreground">Live video interviews appear here after candidates book a slot.</p>
            </div>

            <div className="mb-6 border-b pb-6" data-testid="open-slots-panel">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <h3 className="font-semibold">Open Time Slots</h3>
                  <p className="text-sm text-muted-foreground">These are available for candidates to reserve.</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => loadSlotsForJobs(jobs)} data-testid="refresh-slots-btn">
                  Refresh
                </Button>
              </div>

              {slots.length === 0 ? (
                <div className="border bg-slate-50 p-4 text-sm text-muted-foreground">
                  No open slots yet. Use Add Time Slot on a job listing to create interview availability.
                </div>
              ) : (
                <div className="space-y-3" data-testid="open-slots-list">
                  {slots.slice(0, 12).map((slot) => (
                    <div key={slot.id} className="border p-3" data-testid={`open-slot-${slot.id}`}>
                      <p className="font-medium">{slot.job?.title || 'Job listing'}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatSlotTime(slot.start_time)} - {formatSlotTime(slot.end_time)}
                      </p>
                    </div>
                  ))}
                  {slots.length > 12 && (
                    <div className="border bg-slate-50 p-3 text-sm text-muted-foreground">
                      Showing the next 12 of {slots.length} open slots. Candidates can choose from the full availability list.
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-4" data-testid="bookings-list">
              {bookings.length === 0 ? (
                <div className="border bg-slate-50 p-8 text-center">
                  <Calendar className="mx-auto mb-3 text-slate-400" />
                  <p className="font-medium">No booked interviews yet</p>
                  <p className="mt-1 text-sm text-muted-foreground">Open time slots are shown above and on each job. Booked candidate interviews will appear here.</p>
                </div>
              ) : (
                bookings.map((booking) => (
                  <div key={booking.id} className="border p-4 space-y-2" data-testid={`booking-card-${booking.id}`}>
                    <h3 className="font-semibold">{booking.job?.title}</h3>
                    <p className="text-sm text-muted-foreground">Candidate: {booking.candidate_name || 'N/A'}</p>
                    <Button 
                      size="sm" 
                      onClick={async () => {
                        try {
                          const res = await api.post(`/video/room?booking_id=${booking.id}`);
                          navigate(`/video/${booking.id}`);
                        } catch (error) {
                          toast.error('Failed to create room');
                        }
                      }}
                      data-testid={`join-interview-btn-${booking.id}`}
                    >
                      Join Interview
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white p-6 border" data-testid="ai-interviews-panel">
          <div className="mb-6 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-heading font-semibold">Phase 1 AI Interviews</h2>
              <p className="text-sm text-muted-foreground">
                Review AI-first interviews before advancing candidates to Phase 2 human interviews.
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={loadAIInterviews}>Refresh</Button>
          </div>

          {aiInterviews.length === 0 ? (
            <div className="border bg-slate-50 p-6 text-center">
              <Sparkles className="mx-auto mb-3 text-slate-400" />
              <p className="font-medium">No Phase 1 AI interviews yet</p>
              <p className="mt-1 text-sm text-muted-foreground">Turn on AI Phase 1 in a job listing so candidate reports appear here.</p>
            </div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              {aiInterviews.map((session) => (
                <div key={session.id} className="border p-4" data-testid={`ai-interview-card-${session.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold">{session.candidate_name}</h3>
                      <p className="text-sm text-muted-foreground">{session.job?.title || 'Job listing'}</p>
                      {session.report && (
                        <p className="mt-1 text-xs text-blue-700">
                          {session.report.evaluator?.provider === 'openai' ? 'OpenAI structured evaluation' : 'Secure backup evaluation'}
                        </p>
                      )}
                    </div>
                    <span className="rounded border bg-slate-50 px-2 py-1 text-xs text-slate-700">{session.status}</span>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                    <div className="border bg-blue-50 p-3">
                      <p className="text-xs text-blue-700">AI Score</p>
                      <p className="text-2xl font-bold">{session.score ?? '-'}</p>
                    </div>
                    <div className="border bg-emerald-50 p-3">
                      <p className="text-xs text-emerald-700">Threshold</p>
                      <p className="text-2xl font-bold">{session.report?.threshold ?? session.job?.ai_interview_threshold ?? 75}</p>
                    </div>
                  </div>

                  {session.report && (
                    <div className="mt-3 space-y-3 text-sm">
                      <div>
                        <p className="font-semibold">Strengths</p>
                        <p className="text-muted-foreground">{session.report.strengths?.join(' ')}</p>
                      </div>
                      <div>
                        <p className="font-semibold">Prep Before Phase 2</p>
                        <p className="text-muted-foreground">{session.report.gaps?.join(' ')}</p>
                      </div>
                      <details className="border bg-slate-50 p-3">
                        <summary className="cursor-pointer font-semibold">Transcript and human questions</summary>
                        <div className="mt-3 space-y-3">
                          {(session.answers || []).map((answer) => {
                            const question = (session.questions || []).find((item) => item.id === answer.question_id);
                            return (
                              <div key={answer.question_id}>
                                <p className="font-medium">{question?.prompt || answer.question_id}</p>
                                <p className="text-muted-foreground">{answer.answer}</p>
                              </div>
                            );
                          })}
                          <div>
                            <p className="font-medium">Recommended human follow-ups</p>
                            <ul className="list-disc pl-5 text-muted-foreground">
                              {(session.report.recommended_human_questions || []).map((question) => <li key={question}>{question}</li>)}
                            </ul>
                          </div>
                        </div>
                      </details>
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button size="sm" disabled={!session.submitted_at} onClick={() => reviewAIInterview(session.id, 'advanced')}>
                      {session.submitted_at ? 'Advance to Phase 2' : 'Awaiting Answers'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => reviewAIInterview(session.id, 'request_retake')}>
                      Request Retake
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => reviewAIInterview(session.id, 'needs_review')}>
                      Needs Review
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-8 bg-white p-6 border" data-testid="company-activities-panel">
          <div className="mb-6 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-heading font-semibold">Company Activities</h2>
              <p className="text-sm text-muted-foreground">Create open houses, skill challenges, culture chats, or client discovery moments so people can get to know the company before a formal interview.</p>
            </div>
            <Button size="sm" onClick={() => setShowCreateActivity(true)} data-testid="create-activity-btn">
              <Megaphone size={16} className="mr-2" /> Create Activity
            </Button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="activities-list">
            {activities.length === 0 ? (
              <div className="border bg-slate-50 p-6 text-center md:col-span-2 lg:col-span-3">
                <Megaphone className="mx-auto mb-3 text-slate-400" />
                <p className="font-medium">No activities yet</p>
                <p className="mt-1 text-sm text-muted-foreground">Give candidates and potential clients a low-pressure way to know your team.</p>
              </div>
            ) : activities.map((activity) => (
              <div key={activity.id} className="border p-4" data-testid={`activity-card-${activity.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold">{activity.title}</h3>
                    <p className="text-sm text-muted-foreground">{activity.activity_type} - {activity.location}</p>
                  </div>
                  <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">{activity.interested_count || 0} interested</span>
                </div>
                <p className="mt-2 text-sm">{activity.description}</p>
                <p className="mt-2 text-xs text-muted-foreground">Audience: {activity.audience}</p>
              </div>
            ))}
          </div>
        </div>      </div>


      <Dialog open={showCreateActivity} onOpenChange={setShowCreateActivity}>
        <DialogContent className="max-w-2xl" data-testid="create-activity-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Company Activity</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateActivity} className="space-y-4">
            <div className="grid sm:grid-cols-3 gap-2">
              {activityTemplates.map((template) => (
                <Button key={template.label} type="button" variant="outline" onClick={() => applyActivityTemplate(template)}>{template.label}</Button>
              ))}
            </div>
            <div><Label>Title</Label><Input value={activityForm.title} onChange={(e) => updateActivityForm('title', e.target.value)} required placeholder="Virtual Hiring Open House" /></div>
            <div><Label>Description</Label><Textarea value={activityForm.description} onChange={(e) => updateActivityForm('description', e.target.value)} required rows={4} placeholder="Tell people what they will do, learn, or experience." /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Type</Label><Input value={activityForm.activity_type} onChange={(e) => updateActivityForm('activity_type', e.target.value)} placeholder="Open house, challenge, Q&A" /></div>
              <div><Label>Location</Label><Input value={activityForm.location} onChange={(e) => updateActivityForm('location', e.target.value)} placeholder="Online, Dallas TX, Houston" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Audience</Label><Input value={activityForm.audience} onChange={(e) => updateActivityForm('audience', e.target.value)} /></div>
              <div><Label>Call to Action</Label><Input value={activityForm.call_to_action} onChange={(e) => updateActivityForm('call_to_action', e.target.value)} /></div>
            </div>
            <div><Label>Scheduled Date/Time</Label><Input type="datetime-local" value={activityForm.scheduled_at} onChange={(e) => updateActivityForm('scheduled_at', e.target.value)} /></div>
            <Button type="submit" className="w-full" disabled={loading}>{loading ? 'Creating...' : 'Create Activity'}</Button>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={!!selectedListing} onOpenChange={(open) => !open && setSelectedListing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="view-listing-dialog">
          {selectedListing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">{selectedListing.title}</DialogTitle>
                <div className="flex flex-wrap gap-2 pt-2 text-sm text-muted-foreground">
                  <span className="inline-flex items-center gap-1 rounded border bg-slate-50 px-2 py-1">
                    <MapPin size={14} />
                    {selectedListing.location || 'Location not set'}
                  </span>
                  {selectedListing.salary_range && (
                    <span className="inline-flex items-center gap-1 rounded border bg-slate-50 px-2 py-1">
                      <DollarSign size={14} />
                      {selectedListing.salary_range}
                    </span>
                  )}
                </div>
              </DialogHeader>

              <div className="space-y-5">
                <div>
                  <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">Listing Description</h3>
                  <div className="space-y-3 whitespace-pre-wrap rounded border bg-white p-4 text-sm leading-6 text-slate-800">
                    {selectedListing.description}
                  </div>
                </div>

                {Array.isArray(selectedListing.requirements) && selectedListing.requirements.length > 0 && (
                  <div>
                    <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">Requirements</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedListing.requirements.map((requirement) => (
                        <span key={requirement} className="rounded border bg-slate-50 px-2 py-1 text-xs text-slate-700">
                          {requirement}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 border-t pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelectedJob(selectedListing);
                      setSelectedListing(null);
                      setShowCreateSlot(true);
                    }}
                  >
                    <Clock size={16} className="mr-2" />
                    Add Time Slot
                  </Button>
                  <Button
                    variant="outline"
                    className="bg-amber-50 border-amber-600 hover:bg-amber-100"
                    onClick={() => {
                      const jobId = selectedListing.id;
                      setSelectedListing(null);
                      handleBoostJob(selectedListing);
                    }}
                  >
                    Boost Job
                  </Button>
                  <Button onClick={() => navigate('/browse-candidates')}>
                    <Search size={16} className="mr-2" />
                    Find Candidates
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showCreateJob} onOpenChange={setShowCreateJob}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="create-job-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Job Listing</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Build a listing candidates can understand quickly: what they will do, what matters most, how the interview works, and why the role is worth their time.
            </p>
          </DialogHeader>

          <form onSubmit={handleCreateJob} className="grid gap-5 lg:grid-cols-[1fr_230px]">
            <div className="space-y-4">
              <div>
                <Label htmlFor="job-title">Job Title</Label>
                <Input
                  id="job-title"
                  name="title"
                  required
                  value={jobForm.title}
                  onChange={(e) => updateJobForm('title', e.target.value)}
                  placeholder="Example: Customer Success Specialist"
                  data-testid="job-title-input"
                />
                <p className="mt-1 text-xs text-muted-foreground">Use the title candidates will search for, not an internal nickname.</p>
              </div>

              <div>
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="job-description">Candidate-Facing Description</Label>
                  <Button type="button" size="sm" variant="outline" onClick={improveJobDescription}>
                    <Wand2 size={14} className="mr-2" />
                    Smart Fill
                  </Button>
                </div>
                <Textarea
                  id="job-description"
                  name="description"
                  required
                  rows={4}
                  value={jobForm.description}
                  onChange={(e) => updateJobForm('description', e.target.value)}
                  placeholder="Describe the role in plain language: who they help, what success looks like, and why this opening matters."
                  data-testid="job-description-input"
                />
                <p className="mt-1 text-xs text-muted-foreground">Good listings answer: what is the work, who will they work with, and what happens in the interview?</p>
              </div>

              <div>
                <Label htmlFor="job-responsibilities">Daily Work and Responsibilities</Label>
                <Textarea
                  id="job-responsibilities"
                  name="responsibilities"
                  rows={3}
                  value={jobForm.responsibilities}
                  onChange={(e) => updateJobForm('responsibilities', e.target.value)}
                  placeholder="Example: manage customer follow-ups, update CRM notes, prepare weekly reports, join interview sessions..."
                  data-testid="job-responsibilities-input"
                />
              </div>

              <div>
                <Label htmlFor="job-requirements">Requirements</Label>
                <Input
                  id="job-requirements"
                  name="requirements"
                  required
                  value={jobForm.requirements}
                  onChange={(e) => updateJobForm('requirements', e.target.value)}
                  placeholder="Communication, scheduling, CRM, customer service, Excel"
                  data-testid="job-requirements-input"
                />
                <p className="mt-1 text-xs text-muted-foreground">Separate each skill with a comma so candidates can scan the match quickly.</p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="job-salary" className="flex items-center gap-2">
                    <DollarSign size={14} /> Salary Range
                  </Label>
                  <Input
                    id="job-salary"
                    name="salary_range"
                    value={jobForm.salary_range}
                    onChange={(e) => updateJobForm('salary_range', e.target.value)}
                    placeholder="$80,000 - $120,000"
                    data-testid="job-salary-input"
                  />
                </div>
                <div>
                  <Label htmlFor="job-location" className="flex items-center gap-2">
                    <MapPin size={14} /> Location
                  </Label>
                  <Input
                    id="job-location"
                    name="location"
                    required
                    value={jobForm.location}
                    onChange={(e) => updateJobForm('location', e.target.value)}
                    placeholder="Remote, Dallas TX, Hybrid - Atlanta"
                    data-testid="job-location-input"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="job-workplace">Workplace</Label>
                  <select id="job-workplace" name="workplace_type" value={jobForm.workplace_type} onChange={(e) => updateJobForm('workplace_type', e.target.value)} className="w-full border rounded px-3 py-2">
                    <option>Remote</option>
                    <option>Hybrid</option>
                    <option>On-site</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="job-employment">Employment</Label>
                  <select id="job-employment" name="employment_type" value={jobForm.employment_type} onChange={(e) => updateJobForm('employment_type', e.target.value)} className="w-full border rounded px-3 py-2">
                    <option>Full-time</option>
                    <option>Part-time</option>
                    <option>Contract</option>
                    <option>Temporary</option>
                    <option>Internship</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="job-seniority">Seniority</Label>
                  <select id="job-seniority" name="seniority" value={jobForm.seniority} onChange={(e) => updateJobForm('seniority', e.target.value)} className="w-full border rounded px-3 py-2">
                    <option>Entry-level</option>
                    <option>Mid-level</option>
                    <option>Senior</option>
                    <option>Lead</option>
                    <option>Executive</option>
                  </select>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="job-interview-format">Interview Format</Label>
                  <select id="job-interview-format" name="interview_format" value={jobForm.interview_format} onChange={(e) => updateJobForm('interview_format', e.target.value)} className="w-full border rounded px-3 py-2">
                    <option>Live video interview</option>
                    <option>Resume review first</option>
                    <option>Portfolio or work sample</option>
                    <option>Panel interview</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="job-benefits">Benefits / Perks</Label>
                  <Input
                    id="job-benefits"
                    name="benefits"
                    value={jobForm.benefits}
                    onChange={(e) => updateJobForm('benefits', e.target.value)}
                    placeholder="PTO, bonus, training, remote flexibility"
                    data-testid="job-benefits-input"
                  />
                </div>
              </div>

              <div className="border bg-blue-50 p-4">
                <div className="mb-3 flex items-center gap-2">
                  <Sparkles size={18} className="text-blue-700" />
                  <div>
                    <p className="font-semibold">Phase 1 AI Interview</p>
                    <p className="text-xs text-blue-900">Use AI as the first structured conversation before Phase 2 with a human.</p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="job-ai-mode">AI Phase Mode</Label>
                    <select
                      id="job-ai-mode"
                      name="ai_interview_mode"
                      value={jobForm.ai_interview_mode}
                      onChange={(e) => updateJobForm('ai_interview_mode', e.target.value)}
                      className="w-full border rounded px-3 py-2 bg-white"
                      data-testid="job-ai-mode"
                    >
                      <option value="optional">Optional fast-track</option>
                      <option value="required">Required before Phase 2</option>
                      <option value="off">Off - direct booking</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="job-ai-threshold">Advance Threshold</Label>
                    <Input
                      id="job-ai-threshold"
                      name="ai_interview_threshold"
                      type="number"
                      min="50"
                      max="95"
                      value={jobForm.ai_interview_threshold}
                      onChange={(e) => updateJobForm('ai_interview_threshold', e.target.value)}
                      data-testid="job-ai-threshold"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <Label htmlFor="job-ai-focus">AI Interview Focus</Label>
                  <Input
                    id="job-ai-focus"
                    name="ai_interview_focus"
                    value={jobForm.ai_interview_focus}
                    onChange={(e) => updateJobForm('ai_interview_focus', e.target.value)}
                    placeholder="Reliability, client care, scheduling, communication, CRM..."
                    data-testid="job-ai-focus"
                  />
                </div>
                <div className="mt-4">
                  <Label htmlFor="job-ai-custom">Custom AI Questions</Label>
                  <Textarea
                    id="job-ai-custom"
                    name="ai_custom_questions"
                    rows={3}
                    value={jobForm.ai_custom_questions}
                    onChange={(e) => updateJobForm('ai_custom_questions', e.target.value)}
                    placeholder="One question per line. Example: Tell us about a time you helped a client feel safe and respected."
                    data-testid="job-ai-custom-questions"
                  />
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={loading} data-testid="submit-job-btn">
                {loading ? 'Creating...' : 'Create Job'}
              </Button>
            </div>

            <aside className="space-y-4 border-l pl-4">
              <div>
                <div className="flex items-center gap-2 font-semibold">
                  <Sparkles size={16} />
                  Draft Tools
                </div>
                <p className="mt-1 text-xs text-muted-foreground">Start with a role template, then edit the details for your company.</p>
              </div>

              <div className="space-y-2">
                {jobTemplates.map((template) => (
                  <Button key={template.label} type="button" variant="outline" size="sm" className="w-full justify-start" onClick={() => applyJobTemplate(template)}>
                    <ClipboardList size={14} className="mr-2" />
                    {template.label}
                  </Button>
                ))}
              </div>

              <div className="rounded border bg-blue-50 p-3 text-xs text-blue-950">
                <p className="font-semibold">Listing checklist</p>
                <p className="mt-2">Include pay, location, daily work, interview format, must-have skills, and what makes the role worth applying for.</p>
              </div>

              <div className="rounded border bg-amber-50 p-3 text-xs text-amber-950">
                <p className="font-semibold">Candidate clarity</p>
                <p className="mt-2">Avoid vague phrases like “fast-paced team.” Tell candidates what they will actually do in week one.</p>
              </div>
            </aside>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showCreateSlot} onOpenChange={setShowCreateSlot}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="create-slot-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Interview Availability</DialogTitle>
            <p className="text-sm text-muted-foreground">
              {selectedJob ? `Create times for ${selectedJob.title}.` : 'Choose a job listing to create times.'}
            </p>
          </DialogHeader>

          <div className="grid grid-cols-3 border" aria-label="Scheduling method">
            <button type="button" onClick={() => setSlotMode('single')} className={`px-3 py-2 text-sm font-medium ${slotMode === 'single' ? 'bg-slate-900 text-white' : 'bg-white hover:bg-slate-50'}`}>
              One Time
            </button>
            <button type="button" onClick={() => setSlotMode('bulk')} className={`border-x px-3 py-2 text-sm font-medium ${slotMode === 'bulk' ? 'bg-slate-900 text-white' : 'bg-white hover:bg-slate-50'}`}>
              Multiple Times
            </button>
            <button type="button" onClick={() => setSlotMode('ai')} className={`px-3 py-2 text-sm font-medium ${slotMode === 'ai' ? 'bg-blue-700 text-white' : 'bg-white hover:bg-blue-50'}`}>
              <Sparkles size={14} className="mr-1 inline" /> AI Schedule
            </button>
          </div>

          {slotMode === 'single' && (
            <form onSubmit={handleCreateSlot} className="space-y-4">
              <div>
                <Label htmlFor="slot-start">Start Time</Label>
                <Input id="slot-start" name="start_time" type="datetime-local" required data-testid="slot-start-input" />
              </div>
              <div>
                <Label htmlFor="slot-end">End Time</Label>
                <Input id="slot-end" name="end_time" type="datetime-local" required data-testid="slot-end-input" />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="submit-slot-btn">
                {loading ? 'Creating...' : 'Add One Time'}
              </Button>
            </form>
          )}

          {slotMode === 'ai' && (
            <div className="space-y-4">
              <div className="rounded border border-blue-200 bg-blue-50 p-4">
                <Label htmlFor="ai-schedule-prompt" className="text-blue-950">Describe your interview availability</Label>
                <Textarea
                  id="ai-schedule-prompt"
                  className="mt-2 min-h-24 bg-white"
                  value={aiSchedulePrompt}
                  onChange={(event) => { setAiSchedulePrompt(event.target.value); setAiSlotPreview(null); }}
                  placeholder="Weekdays tomorrow through December 31, 9 AM to 4:30 PM, every 30 minutes with a 10 minute buffer"
                  data-testid="ai-slot-prompt"
                />
                <p className="mt-2 text-xs text-blue-900">Include days, date range, daily hours, interview length, and an optional break between interviews.</p>
              </div>
              <Button type="button" variant="outline" className="w-full border-blue-300 text-blue-800" onClick={previewAISchedule} disabled={loading || !aiSchedulePrompt.trim()} data-testid="preview-ai-slots-btn">
                <Wand2 size={16} className="mr-2" />
                {loading ? 'Building schedule...' : 'Create AI Preview'}
              </Button>
              {!aiSlotPreview && (
                <div className="grid gap-2 text-sm sm:grid-cols-3">
                  <div className="border p-3"><p className="font-medium">Natural language</p><p className="mt-1 text-xs text-muted-foreground">Write the schedule the way you would tell an assistant.</p></div>
                  <div className="border p-3"><p className="font-medium">Preview first</p><p className="mt-1 text-xs text-muted-foreground">Review dates and count before saving.</p></div>
                  <div className="border p-3"><p className="font-medium">Duplicate protection</p><p className="mt-1 text-xs text-muted-foreground">Existing matching times are skipped.</p></div>
                </div>
              )}
            </div>
          )}

          {(slotMode === 'bulk' || aiSlotPreview) && (
            <div className="space-y-4 rounded border p-4" data-testid="bulk-slot-builder">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-semibold">{aiSlotPreview ? 'AI schedule preview' : 'Recurring availability'}</p>
                  <p className="text-xs text-muted-foreground">Times are created only after you confirm below.</p>
                </div>
                {aiSlotPreview?.slot_count && <span className="bg-blue-100 px-3 py-1 text-sm font-semibold text-blue-800">{aiSlotPreview.slot_count} times</span>}
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div><Label htmlFor="bulk-start-date">Starts</Label><Input id="bulk-start-date" type="date" value={bulkSchedule.start_date} onChange={(event) => updateBulkSchedule('start_date', event.target.value)} /></div>
                <div><Label htmlFor="bulk-end-date">Ends</Label><Input id="bulk-end-date" type="date" value={bulkSchedule.end_date} onChange={(event) => updateBulkSchedule('end_date', event.target.value)} /></div>
                <div><Label htmlFor="bulk-start-time">Daily start</Label><Input id="bulk-start-time" type="time" value={bulkSchedule.start_time} onChange={(event) => updateBulkSchedule('start_time', event.target.value)} /></div>
                <div><Label htmlFor="bulk-end-time">Daily end</Label><Input id="bulk-end-time" type="time" value={bulkSchedule.end_time} onChange={(event) => updateBulkSchedule('end_time', event.target.value)} /></div>
                <div><Label htmlFor="slot-duration">Interview length</Label><select id="slot-duration" className="mt-1 h-10 w-full border bg-white px-3 text-sm" value={bulkSchedule.duration_minutes} onChange={(event) => updateBulkSchedule('duration_minutes', Number(event.target.value))}><option value={15}>15 minutes</option><option value={30}>30 minutes</option><option value={45}>45 minutes</option><option value={60}>60 minutes</option><option value={90}>90 minutes</option></select></div>
                <div><Label htmlFor="slot-break">Break between times</Label><select id="slot-break" className="mt-1 h-10 w-full border bg-white px-3 text-sm" value={bulkSchedule.break_minutes} onChange={(event) => updateBulkSchedule('break_minutes', Number(event.target.value))}><option value={0}>No break</option><option value={5}>5 minutes</option><option value={10}>10 minutes</option><option value={15}>15 minutes</option><option value={30}>30 minutes</option></select></div>
              </div>

              <div>
                <Label>Days available</Label>
                <div className="mt-2 grid grid-cols-7 gap-1">
                  {weekdayOptions.map((day) => (
                    <button key={day.value} type="button" onClick={() => toggleScheduleWeekday(day.value)} aria-pressed={bulkSchedule.weekdays.includes(day.value)} className={`h-9 border text-xs font-medium ${bulkSchedule.weekdays.includes(day.value) ? 'border-blue-700 bg-blue-700 text-white' : 'bg-white hover:bg-slate-50'}`}>
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>

              {aiSlotPreview?.preview?.length > 0 && (
                <div className="rounded bg-slate-50 p-3 text-sm">
                  <p className="font-medium">First available times</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {aiSlotPreview.preview.slice(0, 4).map((slot) => <span key={slot.start_time} className="border bg-white px-2 py-1 text-xs">{formatSlotTime(slot.start_time)}</span>)}
                  </div>
                </div>
              )}

              <Button type="button" className="w-full" onClick={handleCreateBulkSlots} disabled={loading} data-testid="submit-bulk-slots-btn">
                <CalendarDays size={16} className="mr-2" />
                {loading ? 'Creating availability...' : aiSlotPreview?.slot_count ? `Create ${aiSlotPreview.slot_count} Time Slots` : 'Create Multiple Time Slots'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>


      <Dialog open={showBoostPackages} onOpenChange={(open) => {
        setShowBoostPackages(open);
        if (!open) setSelectedBoostJob(null);
      }}>
        <DialogContent className="max-w-3xl" data-testid="boost-package-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Choose Boost Package</DialogTitle>
            <p className="text-sm text-muted-foreground">
              {selectedBoostJob ? `Boost ${selectedBoostJob.title} with featured placement and SEO-ready visibility.` : 'Choose a visibility package.'}
            </p>
          </DialogHeader>
          <div className="grid md:grid-cols-3 gap-4">
            {boostPackages.map((pkg) => (
              <button
                key={pkg.days}
                type="button"
                onClick={() => startBoostPackage(pkg)}
                className="border bg-white p-4 text-left hover:border-amber-600 hover:bg-amber-50 transition-colors"
                data-testid={`select-boost-${pkg.days}-day`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-semibold">{pkg.label}</span>
                  <span className="text-2xl font-bold">${pkg.price}</span>
                </div>
                <p className="mt-1 text-sm text-slate-700">{pkg.days} days</p>
                <p className="mt-3 text-sm text-muted-foreground">{pkg.note}</p>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      {boostCheckout && (
        <StripeCheckout
          isOpen={showBoostCheckout}
          onClose={() => {
            setShowBoostCheckout(false);
            setBoostCheckout(null);
          }}
          amount={boostCheckout.amount}
          description={boostCheckout.description}
          clientSecret={boostCheckout.clientSecret}
          onSuccess={handleBoostPaymentSuccess}
        />
      )}

      <Footer />
    </div>
  );
}
